import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Button, ButtonColors, ButtonHeight } from '~/components/ui/Button';
import { CloseButton } from '~/components/ui/CloseButton';
import { TextInput } from '~/components/ui/TextInput';
import { HeaderContainer, Title } from '~/layouts/promo/components/Header';
import {
	StyledContainer,
	StyledArrow,
	StyledInputWrapper,
} from '~/layouts/promo/components/Dropdown.styled';
import { useDropdown } from '~/layouts/promo/hooks/useDropdown';
import { rage, useObservable } from '~/rage';
import { LayoutBuilder } from '~/rage/utils';
import { Layouts } from '~/shared';
import {
	Content,
	Coordinates,
	Field,
	Fields,
	Footer,
	InteriorDropdown,
	InteriorInput,
	Options,
	Option,
	Panel,
	Row,
	Screenshot,
} from './HousesEditor.styled';
import {
	emptyHouseDraft,
	formatHouseLocationRow,
	formatHousePosition,
	getHouseDraftErrors,
	HouseDraft,
	normalizeHousePosition,
} from './housesEditorModel';

// Keep entered values while the panel is hidden to walk or frame a screenshot.
let savedDraft = emptyHouseDraft();

function HouseSelect({
	options,
	label,
	value,
	onChange,
	searchable = false,
}: {
	options: { value: string; label: string }[];
	label: string;
	value: string;
	onChange: (id: string) => void;
	searchable?: boolean;
}) {
	const dropdown = useDropdown(options, value, onChange);
	const open = () => {
		if (!dropdown.isOpen) dropdown.handleToggle();
	};
	useLayoutEffect(() => {
		if (dropdown.isOpen)
			dropdown.listRef?.current
				?.querySelector('[aria-selected="true"]')
				?.scrollIntoView({ block: 'nearest' });
	}, [dropdown.isOpen]);
	return (
		<StyledContainer ref={dropdown.containerRef} style={{ minWidth: 0 }}>
			{searchable ? (
				<InteriorInput
					ref={dropdown.inputRef}
					role="combobox"
					aria-label={label}
					aria-expanded={dropdown.isOpen}
					aria-controls={`house-options-${label}`}
					aria-autocomplete="list"
					placeholder="ID интерьера"
					value={value}
					onFocus={open}
					onClick={open}
					onChange={(event) => {
						onChange(event.target.value);
						dropdown.handleInputChange(event);
						open();
					}}
					onKeyDown={(event) => {
						if (event.key === 'ArrowDown') {
							event.preventDefault();
							if (!dropdown.isOpen) open();
							else
								dropdown.listRef?.current
									?.querySelector<HTMLButtonElement>('button')
									?.focus();
						} else dropdown.handleInputKeyDown(event);
					}}
				/>
			) : (
				<StyledInputWrapper
					as="button"
					type="button"
					role="combobox"
					aria-label={label}
					aria-expanded={dropdown.isOpen}
					aria-controls={`house-options-${label}`}
					$focus={dropdown.isOpen}
					onClick={dropdown.handleToggle}
					style={{ width: '100%' }}
				>
					{dropdown.selectedOption?.label || label}
					<StyledArrow $active={dropdown.isOpen}>▼</StyledArrow>
				</StyledInputWrapper>
			)}
			{dropdown.isOpen && (
				<InteriorDropdown>
					<Options
						id={`house-options-${label}`}
						role="listbox"
						aria-label={label}
						ref={dropdown.listRef}
					>
						{dropdown.filteredOptions.map((option) => (
							<Option
								role="option"
								title={option.label}
								aria-selected={option.value === value}
								key={option.value}
								$selected={option.value === value}
								onClick={() => dropdown.handleSelect(option)}
							>
								{option.label}
							</Option>
						))}
						{!dropdown.filteredOptions.length && (
							<Option disabled>Ничего не найдено</Option>
						)}
					</Options>
				</InteriorDropdown>
			)}
		</StyledContainer>
	);
}

function HousesEditor() {
	const config = useObservable(rage.gameConfig.getConfigs()).houses;
	const previewing = useObservable(rage.housesEditor.getPreviewing());
	const photoActive = useObservable(rage.housesEditor.getPhotoActive());
	const clientBusy = useObservable(rage.housesEditor.getBusy());
	const screenshot = useObservable(rage.housesEditor.getScreenshot());
	const [draft, setDraft] = useState(savedDraft);
	const [pending, setPending] = useState('');
	const running = useRef(false);
	const closing = useRef(false);
	const mounted = useRef(true);
	const [message, setMessage] = useState('');
	const [resetKey, setResetKey] = useState(0);
	const errors = getHouseDraftErrors(draft, config);
	const moving = clientBusy || !!pending;

	useEffect(() => {
		mounted.current = true;
		rage.input
			.setAnyInputFocused(true)
			.catch((error) => rage.notifications.showException(error));
		return () => {
			mounted.current = false;
			rage.input
				.setAnyInputFocused(false)
				.catch((error) => rage.notifications.showException(error));
		};
	}, []);
	const change = (key: keyof HouseDraft, value: string) => {
		savedDraft = { ...savedDraft, [key]: value };
		setDraft(savedDraft);
		setMessage('');
	};
	const run = async (
		label: string,
		action: () => Promise<void>,
		success = '',
	) => {
		if (running.current) return;
		running.current = true;
		setPending(label);
		setMessage('');
		try {
			await action();
			setMessage(success);
		} catch {
			/* The service's notifyOnError decorator displays the error. */
		} finally {
			running.current = false;
			setPending('');
		}
	};
	const reset = () => {
		savedDraft = emptyHouseDraft();
		setDraft(savedDraft);
		setResetKey((key) => key + 1);
		rage.housesEditor.clearScreenshot();
		void run('Возврат', () => rage.housesEditor.returnToStart());
	};
	const button = (
		text: string,
		action: () => void,
		disabled = false,
		primary = false,
	) => (
		<Button
			type="button"
			tabIndex={0}
			height={ButtonHeight.Height40}
			color={primary ? ButtonColors.Select2 : ButtonColors.Default}
			disabled={disabled}
			onClick={action}
		>
			{text}
		</Button>
	);
	return (
		<Panel aria-label="Дома">
			<HeaderContainer>
				<Title>Дома</Title>
				<Row>
					{button('Сброс', reset, !!pending || clientBusy)}
					<div>
						<CloseButton
							height={ButtonHeight.Height40}
							onClick={() => {
								if (closing.current) return;
								closing.current = true;
								void rage.housesEditor
									.close()
									.catch(() => {})
									.finally(() => {
										closing.current = false;
									});
							}}
						/>
					</div>
				</Row>
			</HeaderContainer>
			<Content>
				<Field as="div">
					Интерьер
					<Row>
						<HouseSelect
							key={resetKey}
							label="Интерьер"
							searchable
							options={[
								...new Set(config?.interiors.map((item) => item.id)),
							].map((id) => ({ value: id, label: id }))}
							value={draft.interiorId}
							onChange={(id) => change('interiorId', id)}
						/>
						{button(
							'Войти',
							() => {
								void run('Загрузка', () =>
									rage.housesEditor.preview(draft.interiorId),
								);
							},
							moving ||
								!config?.interiors.some((item) => item.id === draft.interiorId),
						)}
						{button(
							'Вернуться',
							() => {
								void run('Возврат', () => rage.housesEditor.returnToStart());
							},
							moving || !previewing,
						)}
					</Row>
				</Field>
				<Fields>
					<Field as="div">
						Класс
						<HouseSelect
							key={resetKey}
							label="Класс"
							options={
								config?.classes.map((item) => ({
									value: item.id,
									label: item.name,
								})) ?? []
							}
							value={draft.houseClass}
							onChange={(id) => change('houseClass', id)}
						/>
					</Field>
					<Field>
						Цена
						<TextInput
							height={40}
							fontSize={16}
							tabIndex={0}
							type="number"
							min={1}
							step={1}
							placeholder="570000"
							value={draft.price}
							onChange={(event) => change('price', event.target.value)}
						/>
					</Field>
					<Field>
						Парковочных мест
						<TextInput
							height={40}
							fontSize={16}
							tabIndex={0}
							type="number"
							min={0}
							step={1}
							placeholder="5"
							value={draft.parkingSlots}
							onChange={(event) => change('parkingSlots', event.target.value)}
						/>
					</Field>
				</Fields>
				{(['marker', 'spawn'] as const).map((key) => (
					<Coordinates key={key}>
						<label htmlFor={`house-${key}`}>
							{key === 'marker' ? 'Вход' : 'Выход'}
						</label>
						<TextInput
							id={`house-${key}`}
							height={40}
							fontSize={16}
							tabIndex={0}
							placeholder="X, Y, Z"
							value={draft[key]}
							onChange={(event) => change(key, event.target.value)}
							onBlur={(event) =>
								change(key, normalizeHousePosition(event.target.value))
							}
						/>
						{button(
							'Здесь',
							() => {
								void run('Позиция', async () => {
									const previous = savedDraft[key];
									const position = await rage.housesEditor.getPosition();
									if (mounted.current && savedDraft[key] === previous)
										change(key, formatHousePosition(position));
								});
							},
							moving || previewing,
						)}
					</Coordinates>
				))}
				<Row>
					{button(
						photoActive ? 'Выключить камхак' : 'Включить камхак',
						() => {
							void run('Камхак', () =>
								photoActive
									? rage.housesEditor.stopPhoto()
									: rage.housesEditor.preparePhoto(),
							);
						},
						moving,
					)}
					{button(
						'Снимок',
						() => {
							void run('Снимок', () => rage.housesEditor.screenshot());
						},
						!!pending || clientBusy,
					)}
					{button(
						'Копировать снимок',
						() => {
							void run(
								'Копирование',
								() => rage.housesEditor.copyScreenshot(),
								'Снимок скопирован',
							);
						},
						!screenshot?.canCopy || !!pending,
					)}
				</Row>
				{screenshot && <Screenshot src={screenshot.url} alt="Снимок дома" />}
			</Content>
			<Footer>
				<span role="status">{pending ? `${pending}…` : message}</span>
				{button(
					'Копировать строку',
					() => {
						void run(
							'Копирование',
							() =>
								rage.housesEditor.copyLocationRow(
									formatHouseLocationRow(draft),
								),
							'Строка скопирована',
						);
					},
					errors.length > 0 || !!pending,
					true,
				)}
			</Footer>
		</Panel>
	);
}
export default new LayoutBuilder(HousesEditor)
	.setDisplayName(Layouts.HousesEditor)
	.setHudVisible(true)
	.build();
